import type { ViewMode } from "./MarkdownEditor";
import styles from "./MarkdownEditor.module.css";

interface ToolbarButton {
  icon: string;
  label: string;
  format: string;
}

const FORMATTING_BUTTONS: ToolbarButton[] = [
  { icon: "B", label: "Bold", format: "bold" },
  { icon: "I", label: "Italic", format: "italic" },
  { icon: "S", label: "Strikethrough", format: "strikethrough" },
];

const HEADING_BUTTONS: ToolbarButton[] = [
  { icon: "H1", label: "Heading 1", format: "heading1" },
  { icon: "H2", label: "Heading 2", format: "heading2" },
  { icon: "H3", label: "Heading 3", format: "heading3" },
];

const INSERT_BUTTONS: ToolbarButton[] = [
  { icon: "🔗", label: "Link", format: "link" },
  { icon: "🖼", label: "Image", format: "image" },
  { icon: "</>", label: "Inline Code", format: "code" },
  { icon: "{ }", label: "Code Block", format: "codeblock" },
  { icon: '"', label: "Quote", format: "quote" },
];

const LIST_BUTTONS: ToolbarButton[] = [
  { icon: "•", label: "Bulleted List", format: "unorderedList" },
  { icon: "1.", label: "Numbered List", format: "orderedList" },
];

const PODCAST_BUTTONS: ToolbarButton[] = [
  { icon: "⏱", label: "Timestamp", format: "timestamp" },
  { icon: "—", label: "Horizontal Rule", format: "hr" },
];

interface MarkdownToolbarProps {
  onFormat: (format: string) => void;
  viewMode: ViewMode;
  onViewModeChange?: (mode: ViewMode) => void;
  disabled?: boolean;
}

export function MarkdownToolbar({
  onFormat,
  viewMode,
  onViewModeChange,
  disabled,
}: MarkdownToolbarProps) {
  const renderButtonGroup = (buttons: ToolbarButton[]) => (
    <div className={styles.buttonGroup}>
      {buttons.map((btn) => (
        <button
          key={btn.format}
          type="button"
          className={styles.toolbarButton}
          onClick={() => onFormat(btn.format)}
          disabled={disabled || viewMode === "preview"}
          title={btn.label}
          aria-label={btn.label}
        >
          {btn.icon}
        </button>
      ))}
    </div>
  );

  return (
    <div className={styles.toolbar}>
      <div className={styles.toolbarLeft}>
        {renderButtonGroup(FORMATTING_BUTTONS)}
        <div className={styles.divider} />
        {renderButtonGroup(HEADING_BUTTONS)}
        <div className={styles.divider} />
        {renderButtonGroup(INSERT_BUTTONS)}
        <div className={styles.divider} />
        {renderButtonGroup(LIST_BUTTONS)}
        <div className={styles.divider} />
        {renderButtonGroup(PODCAST_BUTTONS)}
      </div>

      {onViewModeChange && (
        <div className={styles.toolbarRight}>
          <div className={styles.viewModeToggle}>
            <button
              type="button"
              className={`${styles.viewModeButton} ${viewMode === "write" ? styles.active : ""}`}
              onClick={() => onViewModeChange("write")}
              disabled={disabled}
              title="Write"
            >
              Write
            </button>
            <button
              type="button"
              className={`${styles.viewModeButton} ${viewMode === "split" ? styles.active : ""}`}
              onClick={() => onViewModeChange("split")}
              disabled={disabled}
              title="Split View"
            >
              Split
            </button>
            <button
              type="button"
              className={`${styles.viewModeButton} ${viewMode === "preview" ? styles.active : ""}`}
              onClick={() => onViewModeChange("preview")}
              disabled={disabled}
              title="Preview"
            >
              Preview
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
